import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiBrightness4, mdiBrightness7, mdiLogout } from "@mdi/js";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
  useColorScheme,
} from "@mui/material";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import {
  createFileRoute,
  ErrorComponent,
  type ErrorComponentProps,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import devTag from "../../../images/dev-tag.png";
import equinorLogoHorizontal from "../../../images/equinor-logo-horizontal.svg";
import homologTag from "../../../images/homolog-tag.png";
import { EnvTag, Logo } from "../../components/organisms/TopMenu/styles";
import { Page403 } from "../../components/templates/Page403";
import { Page404 } from "../../components/templates/Page404";
import { Page500 } from "../../components/templates/Page500";
import { AppError } from "../../errors/AppError";
import { useAuth } from "../../hooks/useAuth";
import { useTopMenu } from "../../hooks/useTopMenu";

const TopMenu: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const { setTopMenuHeight, setTopMenuWidth } = useTopMenu();
  const ref = useRef<HTMLDivElement>(null);

  const navigate = Route.useNavigate();
  const { user, signOut } = useAuth();
  const {
    mode: themeMode,
    systemMode: themeSystemMode,
    setMode,
  } = useColorScheme();

  const [envTag] = useState(() => {
    if (window.environment === "development") {
      return devTag;
    }
    if (window.environment === "homolog") {
      return homologTag;
    }
    return null;
  });
  const [loadingLogout, setLoadingLogout] = useState(false);

  const handleLogout = useCallback(() => {
    setLoadingLogout(true);
    signOut().then(() => {
      router.invalidate().finally(() => {
        // navigate({ to: "/" }); // TODO: Change to home when all routes in React
        navigate({ to: "/users/sign_in" });
      });
    });
  }, [navigate, router, signOut]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      setTopMenuHeight(element.offsetHeight);
      setTopMenuWidth(element.offsetWidth);
    });

    resizeObserver.observe(element);

    setTopMenuHeight(element.offsetHeight);
    setTopMenuWidth(element.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setTopMenuHeight, setTopMenuWidth]);

  return (
    <AppBar
      color="white"
      elevation={0}
      position="sticky"
      ref={ref}
      sx={{
        borderBottom: (currentTheme) =>
          `1px solid ${currentTheme.vars.palette.divider}`,
        zIndex: (currentTheme) => currentTheme.zIndex.drawer + 1,
      }}
    >
      <Box
        component="main"
        sx={{
          display: "grid",
          flexGrow: 1,
          gap: 1,
          gridTemplateAreas: `"logo . header"`,
          gridTemplateColumns: "repeat(3, 1fr)",
          gridTemplateRows: "auto",
          justifyContent: "center",
        }}
      >
        <Toolbar
          sx={{
            gridArea: "logo",
            height: (currentTheme) => currentTheme.mixins.toolbar.minHeight,
          }}
        >
          {!!envTag && <EnvTag src={envTag} />}
          <Logo
            alt="Equinor logo"
            src={`${equinorLogoHorizontal}#${(themeSystemMode ?? themeMode) === "dark" ? "white" : "red"}`}
          />
        </Toolbar>
        <Box
          sx={{
            alignItems: "center",
            display: "grid",
            gap: 2,
            gridArea: "header",
            gridTemplateAreas: `"mode account"`,
            gridTemplateColumns: "auto auto auto",
            gridTemplateRows: "auto",
            justifyContent: "center",
            marginRight: 1,
          }}
        >
          <IconButton
            color="slate"
            onClick={() => setMode(themeMode === "dark" ? "light" : "dark")}
            sx={{ gridArea: "mode", ml: 1 }}
          >
            {themeMode === "dark" ? (
              <Icon icon={mdiBrightness7} />
            ) : (
              <Icon icon={mdiBrightness4} />
            )}
          </IconButton>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography color="slate">{user?.full_name_with_login}</Typography>
            <Box
              component="main"
              sx={{
                display: "grid",
                flexGrow: 1,
                gap: 1,
                gridArea: "actions",
                gridTemplateAreas: `"sign_out"`,
                gridTemplateColumns: "repeat(1, 1fr)",
                gridTemplateRows: "auto",
                justifyContent: "center",
              }}
            >
              <Button
                color="slate"
                loading={loadingLogout}
                onClick={handleLogout}
                size="small"
                startIcon={<Icon icon={mdiLogout} />}
                sx={{
                  gridArea: "sign_out",
                  textTransform: "capitalize",
                  width: "100%",
                }}
                variant="text"
              >
                {t("topMenu.signOut")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </AppBar>
  );
};

const RootComponent: React.FC = () => {
  return (
    <>
      <TopMenu />
      <Outlet />
    </>
  );
};

const ErrorBoundaryComponent: React.FC<ErrorComponentProps> = ({ error }) => {
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    // Reset the query error boundary
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  if (error instanceof AxiosError) {
    if (error.response?.status === 404) {
      return <Page404 />;
    }
    if (error.response?.status === 403) {
      return <Page403 />;
    }

    return <Page500 />;
  }

  if (error instanceof AppError) {
    if (error.status === 403) {
      return <Page403 />;
    }

    return <Page500 />;
  }

  // Fallback to the default ErrorComponent
  return <ErrorComponent error={error} />;
};

export const Route = createFileRoute("/_protected")({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        replace: true,
        search: {
          redirect_uri: location.href,
        },
        to: "/users/sign_in",
      });
    }
  },
  component: RootComponent,
  errorComponent: ErrorBoundaryComponent,
});
